import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { HiArrowRight } from 'react-icons/hi';
import { AiOutlinePlus } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../redux/reducers';
import { store } from '../../../redux/store';
import {
  setSelectedBusiness,
  setSelectedCommunity,
  setSelectedDashboard,
  wipeSelectedBusiness,
  wipeSelectedCommunity,
} from '../../../redux/slices/applicationSlice';
import { setConnections } from '../../../redux/slices/connectionsSlice';
import ApiImage from '../../components/misc/ApiImage';
import { businessTypeIcons } from '../../../utils/iconUtils';
import Icon from '../../components/icons/Icon';
import Business, { BusinessRole, BusinessType } from '../../../types/business';
import WarningModal from '../../components/modals/WarningModal';
import businessService from '../../../services/business/businessService';
import PrimaryButton from '../../components/buttons/PrimaryButton';

export default function BusinessOverviewPage(): JSX.Element {
  const { t } = useTranslation();
  const businesses = useSelector((state: RootState) => state.application.businesses);
  const navigate = useNavigate();

  useEffect(() => {
    businessService.fetchBusinesses();
    store.dispatch(wipeSelectedBusiness());
    store.dispatch(wipeSelectedCommunity());
    store.dispatch(setConnections([]));
  }, []);

  useEffect(() => {
    if (businesses && businesses.length > 0) {
      const activatedBusinesses = businesses.filter(
        (business) =>
          business.dashboard &&
          (business.role === BusinessRole.MANAGER || business.role === BusinessRole.OWNER),
      );

      console.log('Activated businesses:', activatedBusinesses);

      if (activatedBusinesses.length === 1) {
        const business = activatedBusinesses[0];
        if (business.community) {
          store.dispatch(setSelectedCommunity(business.community.id));
        }
        store.dispatch(setSelectedBusiness(business.businessId));
        store.dispatch(setSelectedDashboard(business.dashboard!.dashboardId));
        navigate('/contacts');
      }
    }
  }, [businesses, navigate]);

  return (
    <>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold">{t('page.businessOverview.title')}</h1>
          <p className="py-1 text-gray-600">{t('page.businessOverview.subtitle')}</p>
        </div>
        <PrimaryButton
          text={t('page.businessOverview.new')}
          icon={AiOutlinePlus}
          onClick={() => navigate('/onboarding')}
          className="w-full sm:w-auto"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {/* Activated */}
        {businesses
          ?.filter(
            (business) =>
              business.dashboard &&
              (business.role === BusinessRole.MANAGER || business.role === BusinessRole.OWNER),
          )
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((business, index) => (
            <BusinessCard key={business.businessId ?? `activated-${index}`} business={business} />
          ))}
        {/* Non-activated */}
        {businesses
          ?.filter(
            (business) =>
              !business.dashboard &&
              (business.role === BusinessRole.MANAGER || business.role === BusinessRole.OWNER),
          )
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((business, index) => (
            <BusinessCard
              key={business.businessId ?? `nonactivated-${index}`}
              business={business}
            />
          ))}
      </div>
    </>
  );
}

interface BusinessCardProps {
  business: Business;
}

function BusinessCard({ business }: BusinessCardProps): JSX.Element {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = React.useState(false);
  const { t } = useTranslation();

  const handleBusinessSelect = (businessId: number, dashboardId: number) => {
    if (business.community) {
      store.dispatch(setSelectedCommunity(business.community.id));
    }

    store.dispatch(setSelectedBusiness(businessId));
    store.dispatch(setSelectedDashboard(dashboardId));

    navigate('/contacts');
  };

  const handleClick = () => {
    if (business.dashboard) {
      handleBusinessSelect(business.businessId, business.dashboard.dashboardId);
    } else {
      setModalOpen(true);
    }
  };

  return (
    <>
      <div
        onClick={handleClick}
        className={`group flex cursor-pointer items-center gap-6 rounded-xl border border-primary-900 bg-secondary-50 p-6 transition-all ${!business.dashboard && 'opacity-30'}`}>
        {/* Image/Icon on the left */}
        <div className="flex-shrink-0">
          {business.picture ? (
            <ApiImage
              alt="business-profile"
              path={business.picture}
              className="h-28 w-28 rounded-lg object-cover"
            />
          ) : (
            <div className="flex h-28 w-28 items-center justify-center rounded-lg bg-secondary-200 text-primary-900">
              <Icon
                className="h-14 w-14 text-primary-900"
                icon={
                  businessTypeIcons[business.type] || businessTypeIcons[BusinessType.OTHER_BUSINESS]
                }
              />
            </div>
          )}
        </div>

        {/* Info on the right */}
        <div className="flex min-w-0 flex-1 items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3
              title={business.name}
              className="mb-1 line-clamp-2 break-words text-xl font-semibold text-primary-900">
              {business.name}
            </h3>

            {!business.dashboard && (
              <div className="mt-2">
                <span className="rounded-md bg-orange-100 px-2 py-1 text-xs font-medium text-orange-700">
                  {t('page.businessOverview.notActivated')}
                </span>
              </div>
            )}
          </div>

          {/* Arrow button */}
          <button
            type="button"
            className="flex-shrink-0 rounded-lg bg-primary-300 p-3 transition-all">
            <HiArrowRight className="h-6 w-6 text-primary-900" />
          </button>
        </div>
      </div>
      {!business.dashboard && (
        <WarningModal
          open={modalOpen}
          setOpen={setModalOpen}
          title={t('component.modal.activateDashboard.title', { name: business.name })}
          description={t('component.modal.activateDashboard.subtitle')}
          button={t('component.modal.activateDashboard.button')}
          onContinue={() => navigate('/dashboard/activate', { state: { business } })}
        />
      )}
    </>
  );
}
